import * as React from 'react';
import cx from 'classnames';
import matchSorter from 'match-sorter';
import {
  AttributeHierarchyItem,
  HierarchyAttribute,
  HierarchyCategory,
} from '../../common/types';
import { ItemGroup } from '../CKOptionGroupsMenu';
import Clearbit from '@clearkit/icons/services/Clearbit';
import HubSpot from '@clearkit/icons/glyphs/Hubspot';
import Marketo from '@clearkit/icons/glyphs/Marketo';
import Salesforce from '@clearkit/icons/glyphs/Salesforce';
import SegmentsCategory from '@clearkit/icons/nav/Segments';
import SegmentsAttribute from '@clearkit/icons/glyphs/Segment';
import FolderOpen from '@clearkit/icons/glyphs/FolderOpen';
import Sprocket from '@clearkit/icons/glyphs/Sprocket';
import Trait from '@clearkit/icons/glyphs/Trait';
import View from '@clearkit/icons/glyphs/View';

export type AttributeComparator = (
  a: HierarchyAttribute,
  b: HierarchyAttribute | null
) => boolean;
export const defaultComparator: AttributeComparator = (
  a: HierarchyAttribute,
  b: HierarchyAttribute | null
): boolean => {
  if (!b) {
    return false;
  }

  return (
    a.value === b.value &&
    a.meta.sourceType === b.meta.sourceType &&
    a.meta.objectType === b.meta.objectType
  );
};

function match(attributes: HierarchyAttribute[], query: string) {
  return matchSorter(attributes, query, {
    keys: [(a: AttributeHierarchyItem) => a.label],
  });
}

export function filterAttributes(
  attributes: Array<AttributeHierarchyItem> | undefined,
  query: string | null | undefined,
  iconRenderer: (
    attribute: AttributeHierarchyItem
  ) => React.ReactNode = defaultAccessoryRenderer
): ItemGroup<HierarchyAttribute>[] | HierarchyAttribute[] {
  if (attributes === null || attributes === undefined) {
    return [];
  }

  if (query === null || query === '' || query === undefined) {
    return [];
  }

  if (attributes.some(value => value.type === 'category')) {
    let categories = attributes as HierarchyCategory[];
    const result: ItemGroup<HierarchyAttribute>[] = [];

    categories.forEach(category => {
      const matches = match(getSelectableAttributes(category.children), query);

      if (matches.length > 0) {
        result.push({
          leftIconRenderer: () => iconRenderer(category),
          header: category.label,
          items: matches,
        });
      }
    });

    return result;
  }

  return match(attributes as HierarchyAttribute[], query);
}

export function getSelectableAttributes(
  input: AttributeHierarchyItem[] | null
) {
  let results: HierarchyAttribute[] = [];

  if (!input) {
    return results;
  }

  input.forEach(item => {
    if (item.type === 'attribute') {
      results.push(item);
    } else {
      results = results.concat(getSelectableAttributes(item.children));
    }
  });

  return results;
}

export const defaultAccessoryRenderer = (
  attribute: {
    identifier: string;
  },
  opts?: Partial<{
    isHighlighted: boolean;
    size: number;
  }>
) => {
  const { isHighlighted = false, size = 16 } = opts || {};

  switch (attribute?.identifier) {
    case 'category_segments':
      return (
        <SegmentsCategory
          className={cx({ 'text-blue-500': !isHighlighted })}
          width={size}
          height={size}
        />
      );
    case 'attribute_company_segment':
      return <SegmentsAttribute width={size} height={size} />;
    case 'category_reveal':
      return (
        <View
          style={{ ...(!isHighlighted && { color: '#957fff' }) }}
          width={size}
          height={size}
        />
      );
    case 'category_hubspot':
      return (
        <HubSpot
          style={{ ...(!isHighlighted && { color: '#FF7A59' }) }}
          width={size}
          height={size}
        />
      );
    case 'category_marketo':
      return (
        <Marketo
          style={{ ...(!isHighlighted && { color: '#5C4C9F' }) }}
          width={size}
          height={size}
        />
      );
    case 'category_salesforce':
      return (
        <Salesforce
          style={{ ...(!isHighlighted && { color: '#00A1E0' }) }}
          width={size}
          height={size}
        />
      );
    case 'category_clearbit':
      return (
        <Clearbit
          style={{ ...(!isHighlighted && { color: '#00A1E0' }) }}
          width={size}
          height={size}
        />
      );
    case 'category_collection':
      return <FolderOpen width={size} height={size} />;
    case 'category_custom_traits':
      return <Trait width={size} height={size} />;
    case 'category_system_traits':
      return <Sprocket width={size} height={size} />;
    default:
      return null;
  }
};

export function groupBy<T>(input: T[], groupOn: (val: T) => string) {
  return input.reduce(function(acc, value) {
    (acc[groupOn(value)] = acc[groupOn(value)] || []).push(value);
    return acc;
  }, {} as Record<string, T[]>);
}
